import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
    addSizeCategoryMeasurementApi,
    deleteSizeCategoryMeasurementApi,
    downloadSampleCsvApi,
    exportSizeCategoryMeasurementsApi,
    getSizeCategoryMeasurementsApi,
    getSyncStatusApi,
    importSizeCategoryMeasurementsApi,
    syncNowApi,
    updateSizeCategoryMeasurementApi,
} from "../../../services/manage-product/setting/sizeCategoryMeasurementService";

export const getAllSizeCategoryMeasurements = createAsyncThunk(
    'sizeCategoryMeasurement/getAll',
    async (filters) => {
        const response = await getSizeCategoryMeasurementsApi(filters);
        return response.response;
    }
);

export const addSizeCategoryMeasurement = createAsyncThunk(
    'sizeCategoryMeasurement/add',
    async (data, { dispatch, getState }) => {
        try {
            const response = await addSizeCategoryMeasurementApi(data);
            const result = await response.response;
            dispatch(getAllSizeCategoryMeasurements(getState().product.sizeCategoryMeasurement.filters));
            toast.success("Size category measurement added successfully", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return result;
        } catch (e) {
            return Promise.reject(e);
        }
    }
);

export const updateSizeCategoryMeasurement = createAsyncThunk(
    'sizeCategoryMeasurement/update',
    async (data, { dispatch, getState }) => {
        try {
            const response = await updateSizeCategoryMeasurementApi(data, data.id);
            const result = await response.response;
            dispatch(getAllSizeCategoryMeasurements(getState().product.sizeCategoryMeasurement.filters));
            toast.success("Size category measurement updated successfully", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return result;
        } catch (e) {
            return Promise.reject(e);
        }
    }
);

export const deleteSizeCategoryMeasurement = createAsyncThunk(
    'sizeCategoryMeasurement/delete',
    async (id, { dispatch, getState }) => {
        try {
            const response = await deleteSizeCategoryMeasurementApi(id);
            const result = await response.response;
            dispatch(getAllSizeCategoryMeasurements(getState().product.sizeCategoryMeasurement.filters));
            toast.success("Size category measurement deleted successfully", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return result;
        } catch (e) {
            return Promise.reject(e);
        }
    }
);

export const importSizeCategoryMeasurements = createAsyncThunk(
    'sizeCategoryMeasurement/import',
    async ({ file, sellerId, brandId }, { dispatch, getState }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (sellerId) formData.append('seller_id', sellerId);
        if (brandId)  formData.append('brand_id',  brandId);
        const response = await importSizeCategoryMeasurementsApi(formData);
        const result = response.response;
        if (result.success) {
            dispatch(getAllSizeCategoryMeasurements(getState().product.sizeCategoryMeasurement.filters));
            toast.success(`${result.successCount} record(s) imported successfully`, {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
        return result;
    }
);

export const downloadSampleCsv = createAsyncThunk(
    'sizeCategoryMeasurement/downloadSampleCsv',
    async () => {
        const response = await downloadSampleCsvApi();
        return response.response;
    }
);

export const exportSizeCategoryMeasurements = createAsyncThunk(
    'sizeCategoryMeasurement/export',
    async (filters) => {
        const response = await exportSizeCategoryMeasurementsApi(filters);
        return response.response;
    }
);

export const getSyncStatus = createAsyncThunk(
    'sizeCategoryMeasurement/syncStatus',
    async () => {
        const { response } = await getSyncStatusApi();
        return response;
    }
);

export const syncNow = createAsyncThunk(
    'sizeCategoryMeasurement/syncNow',
    async () => {
        const { response } = await syncNowApi();
        return response;
    }
);

const sizeCategoryMeasurementAdapter = createEntityAdapter({});

export const {
    selectAll: selectSizeCategoryMeasurements,
    selectById: selectSizeCategoryMeasurementById,
} = sizeCategoryMeasurementAdapter.getSelectors(
    (state) => state.product.sizeCategoryMeasurement
);

export const selectSyncStatus = (state) => state.product.sizeCategoryMeasurement.syncStatus;

const sizeCategoryMeasurementSlice = createSlice({
    name: 'sizeCategoryMeasurement',
    initialState: sizeCategoryMeasurementAdapter.getInitialState({
        filters: {},
        syncStatus: { lastSyncedAt: null, pendingCount: 0 },
        addDialog: {
            component: 'SizeCategoryMeasurement',
            type: 'new',
            props: { isOpen: false },
            data: null,
        },
        importDialog: { props: { isOpen: false } },
    }),
    reducers: {
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        openNewDialog: (state) => {
            state.addDialog = {
                component: 'SizeCategoryMeasurement',
                type: 'new',
                props: { isOpen: true },
                data: null,
            };
        },
        openEditDialog: (state, action) => {
            state.addDialog = {
                type: 'edit',
                props: { isOpen: true },
                data: action.payload,
            };
        },
        closeEditDialog: (state) => {
            state.addDialog = {
                type: 'edit',
                props: { isOpen: false },
                data: null,
            };
        },
        openImportDialog: (state) => {
            state.importDialog.props.isOpen = true;
        },
        closeImportDialog: (state) => {
            state.importDialog.props.isOpen = false;
        },
    },
    extraReducers: {
        [getAllSizeCategoryMeasurements.fulfilled]: (state, action) => {
            sizeCategoryMeasurementAdapter.setAll(state, action.payload?.data ?? action.payload ?? []);
        },
        [getSyncStatus.fulfilled]: (state, action) => {
            state.syncStatus = {
                lastSyncedAt: action.payload?.last_synced_at ?? null,
                pendingCount: action.payload?.pending_count ?? 0,
            };
        },
        [syncNow.fulfilled]: (state, action) => {
            state.syncStatus.lastSyncedAt = action.payload?.last_synced_at ?? null;
            state.syncStatus.pendingCount = 0;
        },
    },
});

export const {
    setFilters,
    openNewDialog,
    openEditDialog,
    closeEditDialog,
    openImportDialog,
    closeImportDialog,
} = sizeCategoryMeasurementSlice.actions;

export default sizeCategoryMeasurementSlice.reducer;
